import { useCallback, useEffect, useRef, useState } from "react";
import { apiPost } from "./api";
import { TUTORIAL_VERSION } from "./tourScripts";

// Starts the server-side sandbox session and keeps the tour state. The tour
// boots by itself for a role the user has not completed yet (first entry and
// every role switch) and can be replayed from the cabinet at any time.
const useTrainingTour = ({ me, memberships, cityId, role, refreshMe }) => {
  const [session, setSession] = useState(null);
  const [starting, setStarting] = useState(false);
  const triedRef = useRef(new Set());
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const cityRef = useRef(cityId);
  cityRef.current = cityId;
  const roleRef = useRef(role);
  roleRef.current = role;
  const timerRef = useRef(null);

  const start = useCallback(async (wantedRole) => {
    const targetCity = cityRef.current;
    if (!targetCity || !["passenger", "driver"].includes(wantedRole)) return false;
    setStarting(true);
    try {
      await apiPost("/api/training/start", { cityId: targetCity, role: wantedRole });
      setSession({ role: wantedRole, cityId: targetCity, nonce: Date.now() });
      return true;
    } catch (error) {
      // No active role in this city or the city is not launched: stay quiet,
      // the cabinet explains how to get the role first
      return false;
    } finally {
      setStarting(false);
    }
  }, []);

  const finish = useCallback(
    async (completed) => {
      const current = sessionRef.current;
      if (!current) return;
      sessionRef.current = null;
      setSession(null);
      try {
        await apiPost("/api/training/complete", {
          cityId: current.cityId,
          role: current.role,
          completed: completed === true,
        });
      } catch (error) {
        // The session may have expired; the cabinet replay stays available
      }
      refreshMe?.().catch(() => {});
    },
    [refreshMe]
  );

  // Autostart: first entry into a role and every role switch to a role the
  // user has never trained with. The timer is deliberately not tied to effect
  // cleanup: auth/socket refreshes re-render this tree many times per second
  // right after login, and a cleanup-based timer was silently dropped.
  useEffect(() => {
    if (!me || !cityId || !role || session || starting) return undefined;
    if (!["passenger", "driver"].includes(role)) return undefined;
    const active = memberships.some(
      (membership) =>
        membership.role === role &&
        membership.status === "ACTIVE" &&
        String(membership.cityId?._id ?? membership.cityId) === String(cityId)
    );
    if (!active) return undefined;
    if (Number(me.user?.tutorials?.[role] ?? 0) >= TUTORIAL_VERSION) return undefined;
    const key = `${role}:${cityId}`;
    if (triedRef.current.has(key)) return undefined;
    triedRef.current.add(key);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (sessionRef.current || roleRef.current !== role) return;
      // A failed call (network blip at login) may retry on a later render
      start(role).then((ok) => {
        if (!ok) triedRef.current.delete(key);
      });
    }, 900);
    return undefined;
  }, [me, cityId, role, memberships, session, starting, start]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  return { session, starting, start, finish };
};

export default useTrainingTour;
